import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import WarningIcon from "@mui/icons-material/Warning";
import { red } from "@mui/material/colors";
import Checkbox from "@mui/material/Checkbox";
import { Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import { validateRequired, validateText } from "../validations";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import CircularProgress from '@mui/material/CircularProgress';


const columns = [
  {
    Header: "S.No.",
    accessor: "s_no",
    width: "5%",
  },
  {
    Header: "Discount Code",
    accessor: "discount_code",
    sortable: true,
  },
  {
    Header: "Discount Type",
    accessor: "discount_type",
    sortable: true,
  },
  {
    Header: "Discount Value",
    accessor: "discount_value",
    sortable: true,
  },
  {
    Header: "Applicable Products",
    accessor: "applicable_products",
    sortable: true,
    Cell: ({ row }) => {
      const products = Array.isArray(row.original.applicable_products)
        ? row.original.applicable_products.map(p => p?.name).filter(name => name)
        : [];

      if (products.length === 0) {
        return "N/A";
      } else if (products.length <= 2) {
        return products.join(", ");
      } else {
        const firstTwo = products.slice(0, 2).join(", ");
        const remainingCount = products.length - 2;
        return `${firstTwo}, +${remainingCount} more`;
      }
    },
  },
  {
    Header: "Valid from",
    accessor: "valid_from",
    sortable: true,
  },

  {
    Header: "Valid to",
    accessor: "valid_to",
    sortable: true,
  },
  {
    Header: "Options",
    accessor: "option",
  },
];

function DiscountCode() {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [Input, setInput] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [editInput, setEditInput] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [alldiscounts, setAllDiscounts] = useState([]);
  const [deleteDiscountId, setDeleteDiscountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [safeProductOptions, setSafeProductOptions] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const email = localStorage.getItem("email");


  const icon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkedIcon = <CheckBox fontSize="small" />;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get("/product/product_names", {
          headers: { "Content-Type": "application/json" },
          params: { email },
        });

        if (response.data && response.data.product_names) {
          const options = response.data.product_names.map((product) => ({
            id: product.id || product.product_id,
            name: product.name
          }));
          setSafeProductOptions(options);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [email]);

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };


  function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return "";
    }
    return d.toISOString().split('T')[0];
  }

  useEffect(() => {
    disableBackNavigation();
  }, []);

  const handleClose = () => {
    setInput({});
    setEditInput({});
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setOpenAddModal(false);
    setValidationErrors({});
    setIsEditEnabled(false);
    setIsSaveEnabled(false);
    setPage(0);
  };

  useEffect(() => {
    fetchDiscounts();
    fetchAllDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await api.get("/discount/fetch", {
        params: { email },
      });
      setDiscounts(res.data || []);
      if (page * rowsPerPage >= (res.data || []).length && (res.data || []).length > 0) {
        setPage(0);
      } else if ((res.data || []).length === 0) {
        setPage(0);
      }
    } catch (error) {
      console.error("Error fetching Discounts:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchAllDiscounts = async () => {
    try {
      const res1 = await api.get("/discount/fetch_all", {
        params: { email },
      });
      setAllDiscounts(res1.data || []);
    }
    catch (error) {
      console.error("Error fetching All Discounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (currentInput, isEditForm = false) => {
    let newErrors = {};
    let allFieldsValid = true;

    // Validate discount_code
    if (!currentInput.discount_code) {
      newErrors.discount_code = "Discount code is required.";
      allFieldsValid = false;
    } else {
      const isDuplicate = alldiscounts.some(d =>
        d.discount_code.toLowerCase() === currentInput.discount_code.toLowerCase() &&
        (isEditForm ? d.id !== currentInput.id : true)
      );
      if (isDuplicate) {
        newErrors.discount_code = "Discount code already exists.";
        allFieldsValid = false;
      } else {
        newErrors.discount_code = validateText(currentInput.discount_code);
        if (newErrors.discount_code) allFieldsValid = false;
      }
    }

    // Validate discount_value
    const discountValue = currentInput.discount_value; // Use the value directly as number or null
    if (discountValue === null || discountValue === undefined || discountValue === '') { // Check for empty, null, undefined
      newErrors.discount_value = "Discount value is required.";
      allFieldsValid = false;
    } else if (isNaN(discountValue)) { // Check if it's not a number (e.g. after parseFloat)
        newErrors.discount_value = "Discount value must be a number.";
        allFieldsValid = false;
    } else if (discountValue <= 0 || discountValue > 100) {
      newErrors.discount_value = "Discount value must be between 1 and 100.";
      allFieldsValid = false;
    }

    // Validate valid_from and valid_to
    if (!currentInput.valid_from) {
      newErrors.valid_from = "Valid From date is required.";
      allFieldsValid = false;
    }
    if (!currentInput.valid_to) {
      newErrors.valid_to = "Valid To date is required.";
      allFieldsValid = false;
    }

    if (currentInput.valid_from && currentInput.valid_to) {
      const fromDate = new Date(currentInput.valid_from);
      const toDate = new Date(currentInput.valid_to);
      if (fromDate > toDate) {
        newErrors.valid_from = "'Valid From' cannot be after 'Valid To'.";
        newErrors.valid_to = "'Valid To' cannot be before 'Valid From'.";
        allFieldsValid = false;
      }
    }

    // Validate applicable_products
    if (!Array.isArray(currentInput.applicable_products) || currentInput.applicable_products.length === 0) {
        newErrors.applicable_products = "At least one product must be selected.";
        allFieldsValid = false;
    }

    setValidationErrors(newErrors);

    const hasNoValidationErrors = Object.values(newErrors).every(err => !err);

    if (isEditForm) {
      setIsEditEnabled(allFieldsValid && hasNoValidationErrors);
    } else {
      setIsSaveEnabled(allFieldsValid && hasNoValidationErrors);
    }
    return newErrors;
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "discount_value" && updatedValue !== '') {
        updatedValue = parseFloat(updatedValue);
        if(isNaN(updatedValue)) updatedValue = null;
    } else if (name === "discount_value" && updatedValue === '') {
        updatedValue = null;
    }

    const nextInputState = { ...Input, [name]: updatedValue };

    if (name === "applicable_products" && !Array.isArray(value)) {
      nextInputState[name] = [];
    }

    setInput(nextInputState);
    validateForm(nextInputState, false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "discount_value" && updatedValue !== '') {
        updatedValue = parseFloat(updatedValue);
        if(isNaN(updatedValue)) updatedValue = null;
    } else if (name === "discount_value" && updatedValue === '') {
        updatedValue = null;
    }

    const nextEditInputState = { ...editInput, [name]: updatedValue };

    if (name === "applicable_products" && !Array.isArray(value)) {
      nextEditInputState[name] = [];
    }

    setEditInput(nextEditInputState);
    validateForm(nextEditInputState, true);
  };


  const addDiscount = async (event) => {
    event.preventDefault();

    const errorsAfterSubmit = validateForm(Input, false);
    if (Object.values(errorsAfterSubmit).some(err => !!err)) {
      console.warn("Form has validation errors, preventing submission.");
      return;
    }
    if (!isSaveEnabled) {
      console.warn("Save is not enabled due to validation errors or missing fields.");
      return;
    }

    try {
      const productsToSend = Array.isArray(Input.applicable_products)
        ? Input.applicable_products.map(p => ({ id: p.id, name: p.name }))
        : [];

      const response = await api.post("/discount/add", {
        discount_code: Input.discount_code,
        discount_type: "Percentage",
        discount_value: Input.discount_value,
        valid_from: Input.valid_from,
        valid_to: Input.valid_to,
        applicable_products: productsToSend,
        user_id: email,
      });

      if (response.data.success) {
        Swal.fire({
          title: "Successfully Inserted!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message || "Server Problem. Please Try Again...",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error adding record:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "An unexpected error occurred",
        icon: "error",
      });
    } finally {
      handleClose();
      fetchDiscounts();
      fetchAllDiscounts();
    }
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedDiscounts = discounts.slice(startIndex, endIndex);

  const data = paginatedDiscounts.map((discount, index) => ({
    s_no: startIndex + index + 1,
    discount_code: discount.discount_code,
    discount_type: discount.discount_type,
    discount_value: discount.discount_value,
    valid_from: formatDate(discount.valid_from),
    valid_to: formatDate(discount.valid_to),
    applicable_products: discount.applicable_products,

    option: (
      <div style={{ display: "flex" }}>
        <IconButton
          color="success"
          size="sm"
          onClick={() => {
            const productsForEdit = Array.isArray(discount.applicable_products)
              ? discount.applicable_products.map((product) => ({
                id: product.id || product.product_id,
                name: product.name || "Unnamed Product",
              }))
              : [];

            setEditInput({
              id: discount.id,
              discount_code: discount.discount_code,
              discount_type: discount.discount_type,
              discount_value: discount.discount_value,
              usage_limit: discount.usage_limit,
              minimum_purchase_amount: discount.minimum_purchase_amount,
              valid_from: formatDate(discount.valid_from),
              valid_to: formatDate(discount.valid_to),
              applicable_products: productsForEdit,
            });

            validateForm({
                id: discount.id,
                discount_code: discount.discount_code,
                discount_value: discount.discount_value,
                valid_from: formatDate(discount.valid_from),
                valid_to: formatDate(discount.valid_to),
                applicable_products: productsForEdit,
                usage_limit: discount.usage_limit,
                minimum_purchase_amount: discount.minimum_purchase_amount,
            }, true);

            setOpenEditModal(true);
          }}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          color="error"
          size="sm"
          style={{ marginLeft: "5px" }}
          onClick={() => {
            setDeleteDiscountId(discount.id);
            setOpenDeleteModal(true);
          }}
        >
          <DeleteIcon />
        </IconButton>
      </div>
    ),
  }));

  const deleteDiscount = async (event) => {
    event.preventDefault();
    if (deleteDiscountId) {
      try {
        const response = await api.delete(
          `/discount/delete/${deleteDiscountId}`
        );
        console.log(response.data.success);
        setOpenDeleteModal(false);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            text: "Successfully deleted...",
            type: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          fetchDiscounts();
        } else {
          Swal.fire({
            icon: "error",
            text:
              response.data.message || "Server Problem. Please Try Again...",
            type: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Error deleting Discount:", error);
        Swal.fire({
          icon: "error",
          text: "An unexpected error occurred. Please try again.",
          icon: "error",
        });
      }
    } else {
      console.error("No discount ID provided for deletion.");
    }
  };

  const updateDiscount = async (event) => {
    event.preventDefault();
    console.log("Edit Input:", editInput);

    const errorsAfterSubmit = validateForm(editInput, true);
    if (Object.values(errorsAfterSubmit).some(err => !!err)) {
      console.warn("Form has validation errors, preventing submission.");
      return;
    }
    if (!isEditEnabled) {
      console.warn("Edit is not enabled due to validation errors or missing fields.");
      return;
    }

    try {
      const productsToSend = Array.isArray(editInput.applicable_products)
        ? editInput.applicable_products.map((product) => ({
          id: product.id || null,
          name: product.name,
        }))
        : [];

      const res = await api.put(`/discount/edit/${editInput.id}`, {
        discount_code: editInput.discount_code,
        discount_type: "Percentage",
        discount_value: editInput.discount_value,
        usage_limit: editInput.usage_limit,
        minimum_purchase_amount: editInput.minimum_purchase_amount,
        valid_from: editInput.valid_from,
        valid_to: editInput.valid_to,
        applicable_products: productsToSend,
      });

      if (res.data.success === true) {
        Swal.fire({
          title: "Successfully Updated!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: res.data.msg,
          type: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error updating discount allocation:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred. Please try again.",
        icon: "error",
      });
    } finally {
      handleClose();
      fetchDiscounts();
      fetchAllDiscounts();
    }
  };


  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Discount Table
                </MDTypography>
              </MDBox>
              <MDBox sx={{ textAlign: "right", mt: 4, mr: 4 }}>
                <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                  Add Discount
                </MDButton>
              </MDBox>

              {/* Add Modal */}
              <Dialog
                open={openAddModal}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
              >
                <DialogTitle>Add Discount</DialogTitle>
                <MDBox component="form">
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Discount Code"
                            name="discount_code"
                            value={Input.discount_code || ''}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.discount_code}
                            helperText={validationErrors.discount_code}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Discount Type"
                            name="discount_type"
                            value="Percentage"
                            onChange={handleAddChange}
                            fullWidth
                            disabled
                            margin="normal"
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Discount Value"
                            name="discount_value"
                            type="number"
                            value={Input.discount_value === null ? '' : Input.discount_value}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.discount_value}
                            helperText={validationErrors.discount_value}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          options={safeProductOptions}
                          getOptionLabel={(option) => option.name}
                          value={Input.applicable_products || []}
                          name="applicable_products"
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(event, newValue) => {
                            const processedProducts = newValue.map((product) => ({
                              id: product?.id,
                              name: product?.name,
                            }));
                            handleAddChange({
                              target: {
                                name: "applicable_products",
                                value: processedProducts,
                              },
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Applicable Products"
                              error={!!validationErrors.applicable_products}
                              helperText={validationErrors.applicable_products}
                              placeholder="Select products"
                              fullWidth
                              margin="normal"
                              sx={{
                                "& .MuiInputBase-root": {
                                  minHeight: 45,
                                  alignItems: 'flex-start',
                                  paddingTop: '8px',
                                  paddingBottom: '8px',
                                },
                                "& .MuiInputBase-input": {
                                  padding: "10px 12px",
                                },
                                "& .MuiAutocomplete-inputRoot": {
                                    flexWrap: 'wrap',
                                    paddingRight: '39px !important',
                                },
                              }}
                            />
                          )}
                          sx={{
                            "& .MuiAutocomplete-tag": {
                              backgroundColor: "green",
                              color: "black",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDBox>
                          <TextField
                            label="Valid From"
                            type="date"
                            name="valid_from"
                            value={Input.valid_from || ''}
                            onChange={(e) =>
                              handleAddChange({
                                target: {
                                  name: "valid_from",
                                  value: e.target.value,
                                },
                              })
                            }
                            fullWidth
                            margin="normal"
                            error={!!validationErrors.valid_from}
                            helperText={validationErrors.valid_from}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              min: format(new Date(), "yyyy-MM-dd"),
                            }}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Valid To"
                          type="date"
                          name="valid_to"
                          value={Input.valid_to || ''}
                          onChange={(e) =>
                            handleAddChange({
                              target: {
                                name: "valid_to",
                                value: e.target.value,
                              },
                            })
                          }
                          fullWidth
                          margin="normal"
                          error={!!validationErrors.valid_to}
                          helperText={validationErrors.valid_to}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: Input.valid_from || format(new Date(), "yyyy-MM-dd"),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleClose} color="primary">
                      Cancel
                    </Button>
                    <MDButton
                      type="button"
                      onClick={addDiscount}
                      color="success"
                      disabled={!isSaveEnabled}
                    >
                      Save
                    </MDButton>
                  </DialogActions>
                </MDBox>
              </Dialog>

              {/* Edit Modal */}
              <Dialog open={openEditModal} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Edit Discount Code</DialogTitle>

                <MDBox component="form" onSubmit={updateDiscount} noValidate>
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Discount Code"
                          name="discount_code"
                          value={editInput.discount_code || ''}
                          onChange={handleEditChange}
                          fullWidth
                          margin="normal"
                          error={!!validationErrors.discount_code}
                          helperText={validationErrors.discount_code}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Discount Value"
                          name="discount_value"
                          type="number"
                          value={editInput.discount_value === null ? '' : editInput.discount_value}
                          onChange={handleEditChange}
                          fullWidth
                          margin="normal"
                          error={!!validationErrors.discount_value}
                          helperText={validationErrors.discount_value}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={safeProductOptions}
                          getOptionLabel={(option) => option.name || "Unnamed Product"}
                          name="applicable_products"
                          value={Array.isArray(editInput.applicable_products) ? editInput.applicable_products : []}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          onChange={(event, newValue) => {
                            const uniqueProducts = Array.from(
                              new Map(newValue.map((product) => [product.id, product])).values()
                            );
                            handleEditChange({
                              target: {
                                name: "applicable_products",
                                value: uniqueProducts,
                              },
                            });
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.name}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Applicable Products"
                              placeholder="Select products"
                              error={!!validationErrors.applicable_products}
                              helperText={validationErrors.applicable_products}
                              fullWidth
                              margin="normal"
                              sx={{
                                "& .MuiInputBase-root": {
                                  minHeight: 45,
                                  alignItems: 'flex-start',
                                  paddingTop: '8px',
                                  paddingBottom: '8px',
                                },
                                "& .MuiInputBase-input": {
                                  padding: "10px 12px",
                                },
                                "& .MuiAutocomplete-inputRoot": {
                                    flexWrap: 'wrap',
                                    paddingRight: '39px !important',
                                },
                              }}
                            />
                          )}
                          sx={{
                            "& .MuiAutocomplete-tag": {
                              backgroundColor: "green",
                              color: "black",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Valid From"
                          type="date"
                          name="valid_from"
                          value={editInput.valid_from || ''}
                          onChange={handleEditChange}
                          fullWidth
                          margin="normal"
                          error={!!validationErrors.valid_from}
                          helperText={validationErrors.valid_from}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: format(new Date(), "yyyy-MM-dd") }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Valid To"
                          type="date"
                          name="valid_to"
                          value={editInput.valid_to || ''}
                          onChange={handleEditChange}
                          fullWidth
                          margin="normal"
                          error={!!validationErrors.valid_to}
                          helperText={validationErrors.valid_to}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: editInput.valid_from || format(new Date(), "yyyy-MM-dd") }}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>

                  <DialogActions sx={{ padding: "16px" }}>
                    <MDButton onClick={handleClose} color="secondary" variant="outlined">
                      Cancel
                    </MDButton>
                    <MDButton
                      type="button"
                      onClick={updateDiscount}
                      disabled={!isEditEnabled}
                      color="success"
                      variant="gradient"
                    >
                      Save
                    </MDButton>
                  </DialogActions>
                </MDBox>
              </Dialog>
              {/* Delete Modal */}
              <Dialog open={openDeleteModal} onClose={handleClose}>
                <MDBox component="form" onSubmit={deleteDiscount}>
                  <DialogTitle>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <WarningIcon
                          style={{ color: red[700], fontSize: 40 }}
                        />
                      </Grid>
                      <Grid item>
                        <MDTypography variant="h6" component="span">
                          Confirm Delete
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </DialogTitle>

                  <DialogContent>
                    <MDBox
                      p={2}
                      style={{
                        backgroundColor: red[50],
                        borderLeft: `5px solid ${red[700]}`,
                      }}
                    >
                      <MDTypography variant="body1">
                        Are you sure you want to delete this item?.
                      </MDTypography>
                    </MDBox>
                  </DialogContent>
                  <DialogActions>
                    <MDButton onClick={handleClose} style={{ color: "gray" }}>
                      Cancel
                    </MDButton>
                    <MDButton type="submit" color="primary">
                      Delete
                    </MDButton>
                  </DialogActions>
                </MDBox>
              </Dialog>

              <MDBox p={3}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <MDBox sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <DataTable
                            table={{ columns: columns, rows: data }}
                            isSorted={true}
                            entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                            showTotalEntries={true}
                            canSearch={true}
                            noEndBorder
                            pagination={{
                                variant: "contained",
                                color: "dark",
                                page: page,
                                onPageChange: handlePageChange,
                                rowsPerPage: rowsPerPage,
                                onRowsPerPageChange: handleRowsPerPageChange,
                                count: discounts.length,
                            }}
                        />
                    </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default DiscountCode;